import { ClimateData, ClimateIndicator } from "../types";
import HeroCard from "./HeroCard";
import styles from "../neighbourhoodSection.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { LeafIcon, FloodIcon, EarthquakeIcon, SnowflakeIcon, FireIcon } from "../icons";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import ArrowUpRed from "@Icons/ArrowUpRed";
import ArrowDownGreen from "@Icons/ArrowDownGreen";
import ArrowDownRed from "@Icons/ArrowDownRed";
import InfoIcon from "@Icons/InfoIcon";
import { ReactNode } from "react";
import { resolveCopy, renderBoldText } from "../utils";

const KEY_PREFIX = "propertypage.components.neighbourhood.climate";

const CLIMATE_ICON_MAP: Record<string, ReactNode> = {
  [`${KEY_PREFIX}.badge.airQuality`]: <LeafIcon />,
  [`${KEY_PREFIX}.badge.flood`]: <FloodIcon />,
  [`${KEY_PREFIX}.badge.earthquake`]: <EarthquakeIcon />,
  [`${KEY_PREFIX}.badge.snowfall`]: <SnowflakeIcon />,
  [`${KEY_PREFIX}.badge.wildfire`]: <FireIcon />,
};

type ClimateTabProps = {
  data: ClimateData;
};

const ClimateTab = ({ data }: ClimateTabProps) => {
  const { t } = useTranslationHook();

  const getValueClass = (level: string) => {
    switch (level) {
      case "low":
        return styles.climateValueLow;
      case "moderate":
      case "high":
        return styles.climateValueModerate;
      default:
        return "";
    }
  };

  const resolveIndicatorValue = (indicator: ClimateIndicator) => {
    if (indicator.value.kind === "key") {
      return resolveCopy(t, indicator.value.key);
    }
    return indicator.value.text;
  };

  const tempLabel = resolveCopy(t, data.tempLabelKey);
  const source = resolveCopy(t, data.sourceKey);

  return (
    <div className={styles.contentArea}>
      <HeroCard
        image={data.heroImage}
        stat={data.avgTemp}
        label={tempLabel}
        alt="Climate neighbourhood view"
      />
      <div className={styles.detailsSection}>
        <div>
          {data.indicators.length > 0 && (
            <div className={styles.climateIndicators}>
              {data.indicators.map((indicator) => (
                <div key={indicator.labelKey} className={styles.climateIndicator}>
                  <div className={styles.climateIcon}>{CLIMATE_ICON_MAP[indicator.labelKey]}</div>
                  <span className={styles.climateLabel}>{resolveCopy(t, indicator.labelKey)}</span>
                  <span
                    className={formatClassNames(
                      styles.climateValue,
                      getValueClass(indicator.level),
                    )}
                  >
                    {resolveIndicatorValue(indicator)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {(() => {
            let renderedCount = 0;
            return data.facts.map((fact, index) => {
              const statement = resolveCopy(t, fact.key, fact.params);
              if (!statement) return null;
              const isFirst = renderedCount === 0;
              renderedCount += 1;

              // Special case: cold winter gets ArrowDownRed instead of ArrowDownGreen
              const isColdWinter = fact.key === `${KEY_PREFIX}.fact.winter.cold`;

              return (
                <div key={fact.key}>
                  <div
                    className={formatClassNames(styles.tableRow, isFirst && styles.tableRowFirst)}
                  >
                    <span className={styles.tableText}>{renderBoldText(statement)}</span>
                    <span className={styles.tableIcon} aria-hidden="true">
                      {fact.trend === "up" && <ArrowUpRed />}
                      {fact.trend === "down" &&
                        (isColdWinter ? <ArrowDownRed /> : <ArrowDownGreen />)}
                    </span>
                  </div>
                  {index < data.facts.length - 1 && <div className={styles.tableDivider} />}
                </div>
              );
            });
          })()}
        </div>
        <p className={styles.sourceText}>
          <span className={styles.sourceIcon} aria-hidden="true">
            <InfoIcon width="14" height="14" fill="#6b7280" />
          </span>
          {source}
        </p>
      </div>
    </div>
  );
};

export default wrapperHOC(ClimateTab, {
  componentName: "ClimateTab",
  showForChina: false,
  showForWhiteLable: false,
});
